import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject, type TransitionEvent } from "react"
import { useReducedMotion } from "framer-motion"

/** Keep in sync with the duration class on the animated box. */
export const SWAP_HEIGHT_DURATION_MS = 200

/** ~ the animation window at 60fps, used while the target height is still unknown. */
const CHASE_FRAMES = 12

export type SwapHeightAnimation = {
  /** Box that carries the height transition. */
  boxRef: RefObject<HTMLDivElement | null>
  /** Wrapper around the swapped content; its natural height drives the animation. */
  contentRef: RefObject<HTMLDivElement | null>
  animating: boolean
  onTransitionEnd: (event: TransitionEvent<HTMLElement>) => void
}

/**
 * Animates a box height when its content is swapped: pins the height the outgoing content had, then
 * releases it to the height of the incoming one. Between swaps the height is unset, so content
 * growing on its own (typing) stays instant and never gets clipped.
 *
 * Heights are remembered per mode, because freshly mounted content is not measurable right away
 * (the composer editor mounts asynchronously and claims its min-height a few frames later).
 */
export function useSwapHeightAnimation(mode: string): SwapHeightAnimation {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const heightsRef = useRef(new Map<string, number>())
  const lastWidthRef = useRef(0)
  const currentModeRef = useRef(mode)
  const animatingRef = useRef(false)
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [animating, setAnimating] = useState(false)
  const reducedMotion = useReducedMotion()

  const rememberCurrentHeight = useCallback(() => {
    const content = contentRef.current
    if (!content) return
    const width = content.offsetWidth
    if (width !== lastWidthRef.current) {
      // Column resized: heights remembered for the other modes no longer hold.
      lastWidthRef.current = width
      heightsRef.current.clear()
    }
    heightsRef.current.set(currentModeRef.current, content.offsetHeight)
  }, [])

  const release = useCallback(() => {
    if (releaseTimerRef.current != null) {
      clearTimeout(releaseTimerRef.current)
      releaseTimerRef.current = null
    }
    // ResizeObserver may have delivered the incoming resize while animation was active. Capture the
    // settled natural height explicitly so the first reverse swap always has a non-zero `from`.
    rememberCurrentHeight()
    const box = boxRef.current
    if (box) box.style.height = ""
    animatingRef.current = false
    setAnimating(false)
  }, [rememberCurrentHeight])

  // Safety net for a transition that never ends (interrupted, or no visual change after all).
  const scheduleRelease = useCallback(() => {
    if (releaseTimerRef.current != null) clearTimeout(releaseTimerRef.current)
    releaseTimerRef.current = setTimeout(release, SWAP_HEIGHT_DURATION_MS + 80)
  }, [release])

  // The inner content keeps its natural height even while the outer box clips the transition, so
  // observing it keeps the per-mode cache current without constraining content growth.
  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(rememberCurrentHeight)
    observer.observe(content)
    return () => observer.disconnect()
  }, [rememberCurrentHeight])

  useEffect(() => release, [release])

  useLayoutEffect(() => {
    const previousMode = currentModeRef.current
    if (previousMode === mode) return
    currentModeRef.current = mode
    const box = boxRef.current
    const content = contentRef.current
    if (!box || !content) return
    // During a rapid reversal, continue from the currently rendered transition frame. Once the
    // previous transition has settled, use its explicitly remembered natural height.
    const from = animatingRef.current ? box.getBoundingClientRect().height : (heightsRef.current.get(previousMode) ?? 0)
    if (reducedMotion || from === 0) return

    box.style.height = `${from}px`
    box.getBoundingClientRect() // flush styles so the transition starts from `from`, not from the target
    animatingRef.current = true
    setAnimating(true)

    let target = 0
    const retarget = (height: number) => {
      if (height === 0 || height === target) return
      target = height
      box.style.height = `${height}px`
      scheduleRelease()
    }

    const known = heightsRef.current.get(mode)
    if (known != null) {
      retarget(known)
      return
    }

    // First swap into this mode: measure across the animation window, since content mounting over
    // the next frames would make a single measurement land on an already stale height.
    let frame = 0
    let ticks = 0
    const chase = () => {
      retarget(content.offsetHeight)
      ticks += 1
      if (ticks < CHASE_FRAMES) frame = requestAnimationFrame(chase)
    }
    retarget(content.offsetHeight)
    frame = requestAnimationFrame(chase)
    return () => cancelAnimationFrame(frame)
  }, [mode, reducedMotion, scheduleRelease])

  return {
    boxRef,
    contentRef,
    animating,
    onTransitionEnd: (event) => {
      if (event.target !== event.currentTarget || event.propertyName !== "height") return
      release()
    },
  }
}
